import { useState } from "react";
import { ChevronDownIcon, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { filterTriggerClass } from "./filter-trigger";
import DynamicIcon from "../utils/lucide-icon";
import type { Filter } from "./table-filters";

/** Valor en la URL de un filtro multi_select: valores separados por coma. */
export function parseMultiSelectValue(raw: unknown): string[] {
  if (typeof raw !== "string" || raw === "" || raw === "all") return [];
  return raw
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

export function serializeMultiSelectValue(values: string[]): string | undefined {
  return values.length > 0 ? values.join(",") : undefined;
}

/** Con más opciones que esto, el popover muestra el buscador. */
const SEARCH_FROM = 7;

/**
 * Selector múltiple de la barra de filtros de las tablas (`type:
 * "multi_select"`). Popover con casillas (y buscador si hay muchas opciones);
 * el trigger tiene el mismo aspecto que los selectores simples. Las opciones
 * aceptan `keywords` (texto para el buscador cuando `label` es un nodo de
 * React) y `dotClass` (punto de color, p. ej. el de la columna del kanban).
 */
export function MultiSelectFilter({
  filter,
  value,
  onChange,
  fullWidth = false,
}: {
  filter: Filter;
  value: string[];
  onChange: (values: string[]) => void;
  fullWidth?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const options = (filter.options ?? []).filter((o) => o.value !== "all");
  const selected = new Set(value);
  const selectedOptions = options.filter((o) => selected.has(o.value));

  const toggle = (optionValue: string) => {
    onChange(
      selected.has(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={filter.name}
          className={cn(
            "flex items-center justify-between gap-2 px-2.5 py-1 text-sm whitespace-nowrap outline-none focus-visible:ring-ring/30 focus-visible:ring-[3px] [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5 [&_svg:not([class*='text-'])]:text-muted-foreground",
            filterTriggerClass(value.length > 0),
            fullWidth ? "w-full" : "w-fit min-w-[70px] max-w-[220px]",
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {filter.icon && <DynamicIcon name={filter.icon} />}
            {selectedOptions.some((o) => o.dotClass) && (
              <span className="flex shrink-0 items-center -space-x-0.5">
                {selectedOptions.slice(0, 4).map((o) => (
                  <span
                    key={o.value}
                    className={cn("h-2 w-2 rounded-full ring-1 ring-background", o.dotClass ?? "bg-muted-foreground/50")}
                  />
                ))}
              </span>
            )}
            {selectedOptions.length === 1 ? (
              <span className="min-w-0 truncate">
                {selectedOptions[0].label}
              </span>
            ) : (
              <span
                className="truncate"
              >
                {filter.name}
              </span>
            )}
            {value.length > 1 && (
              <Badge
                variant="secondary"
                className="h-4 min-w-4 rounded-sm px-1 text-[10px] font-medium"
              >
                {value.length}
              </Badge>
            )}
          </span>
          <ChevronDownIcon className="size-3.5 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 overflow-hidden p-0" align="start">
        <Command>
          {options.length >= SEARCH_FROM && (
            <CommandInput placeholder={`Buscar ${filter.name.toLowerCase()}…`} />
          )}
          <CommandList className="min-h-0 max-h-64 sm:min-h-0 sm:max-h-64">
            <CommandEmpty>Sin resultados</CommandEmpty>
            <CommandGroup heading={filter.name}>
              {options.map((option) => {
                const checked = selected.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    // cmdk filtra por `value`: texto buscable de la opción.
                    value={`${option.keywords ?? ""} ${option.value}`}
                    onSelect={() => toggle(option.value)}
                    className={cn("gap-2 py-1.5", checked && "bg-accent/40 font-medium text-foreground")}
                  >
                    <Checkbox
                      checked={checked}
                      className="pointer-events-none"
                      aria-hidden
                      tabIndex={-1}
                    />
                    {option.dotClass && (
                      <span className={cn("h-2 w-2 shrink-0 rounded-full", option.dotClass)} />
                    )}
                    <span className="min-w-0 flex-1 truncate">
                      {option.label}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {options.length > 1 && (
            <div className="flex items-center justify-between gap-1 border-t border-border/50 px-1 py-1">
              <button
                type="button"
                disabled={value.length === options.length}
                onClick={() => onChange(options.map((o) => o.value))}
                className="rounded-sm px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
              >
                Todos
              </button>
              <button
                type="button"
                disabled={value.length === 0}
                onClick={() => onChange([])}
                className="flex items-center gap-1 rounded-sm px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
              >
                <X className="size-3" />
                Limpiar{value.length > 0 ? ` (${value.length})` : ""}
              </button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
